import { BefehlDto } from '../dtos/befehl-dto'

/**
 * Converts an ArrayBuffer to a hexadecimal string.
 */
function bufferToHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Generates a stable, unique frontend ID for a Befehl.
 * The ID is based on:
 * - The complete Befehl content
 * - The parent ID (Befehlsnachricht, Muster, or Auftrag)
 * - The position of the Befehl in the parent's befehle array
 *
 * This ensures uniqueness even when:
 * - The same Befehl appears multiple times in the same parent
 * - The same Befehl appears in different parents
 *
 * This ID is suitable for use as a React key because:
 * - It's stable: The same combination always generates the same ID
 * - It's unique: Different combinations generate different IDs
 *
 * @param befehl The BefehlDto to generate an ID for
 * @param parentId The ID of the parent (Befehlsnachricht, Muster, or Auftrag)
 * @param befehlIndexInParent The position of this Befehl in the parent's befehle array
 * @returns A promise that resolves to a unique hexadecimal string ID
 */
export async function generateBefehlFrontendId(
  befehl: BefehlDto,
  parentId: string,
  befehlIndexInParent: number
): Promise<string> {
  const contentString = JSON.stringify({
    befehl: befehl,
    parentId: parentId,
    befehlIndexInParent: befehlIndexInParent
  })

  const encoder = new TextEncoder()
  const encodedData = encoder.encode(contentString)
  const hashBuffer = await crypto.subtle.digest('SHA-256', encodedData)
  return bufferToHex(hashBuffer)
}
